import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
  select,
} from '@ngrx/store';
import { pipe } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { AllActionsUnion } from '../../../app.actions';
import { Restaurant } from '../../../models/restaurant/restaurant';
import { nonNullFilter } from '../../../utils/non-null-filter';
import { DEFAULT_SEATS } from '../constants';
import {
  resetState,
  storeRestaurant,
  storeSeats,
  storeSelectedTimeSlotId,
  updateLoadingState,
} from './restaurant.actions';

export type LoadingState = 'none' | 'loading' | 'loaded';

export interface RestaurantState {
  loadingState: LoadingState;
  restaurant: Restaurant | null;
  seats: number;
  selectedTimeSlotId: string;
}

const initialState: RestaurantState = {
  loadingState: 'none',
  restaurant: null,
  seats: DEFAULT_SEATS,
  selectedTimeSlotId: '',
};

export const featureName = 'restaurantFeature';

export function restaurantReducer(
  state_: RestaurantState | undefined,
  action: AllActionsUnion,
): RestaurantState {
  const reducer_ = createReducer<RestaurantState>(
    initialState,
    on(
      updateLoadingState,
      (state, { payload }): RestaurantState => {
        return { ...state, loadingState: payload };
      },
    ),
    on(
      storeRestaurant,
      (state, { payload }): RestaurantState => {
        return { ...state, restaurant: payload };
      },
    ),
    on(
      storeSelectedTimeSlotId,
      (state, { payload }): RestaurantState => {
        return { ...state, selectedTimeSlotId: payload };
      },
    ),
    on(
      storeSeats,
      (state, { payload }): RestaurantState => {
        return { ...state, seats: payload };
      },
    ),
    on(
      resetState,
      (): RestaurantState => {
        return { ...initialState };
      },
    ),
  );
  return reducer_(state_, action);
}

const getFeature = createFeatureSelector<RestaurantState>(featureName);

export const getLoadingState = pipe(
  select(
    createSelector(
      getFeature,
      v => v.loadingState,
    ),
  ),
  distinctUntilChanged(),
);

export const getRestaurant = pipe(
  select(
    createSelector(
      getFeature,
      v => v.restaurant,
    ),
  ),
  nonNullFilter(),
  distinctUntilChanged(),
);

export const getSelectedTimeSlot = pipe(
  select(
    createSelector(
      getFeature,
      v => {
        if (v.restaurant === null) {
          return null;
        }
        return v.restaurant.timeSlots.selectById(v.selectedTimeSlotId);
      },
    ),
  ),
  distinctUntilChanged((a, b) => {
    if (a === null || b === null) {
      return false;
    }
    return a.eq(b);
  }),
);

export const getSeats = pipe(
  select(
    createSelector(
      getFeature,
      v => v.seats,
    ),
  ),
  distinctUntilChanged(),
);
